import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import doctorSignatureService from '../../services/doctorSignatureService';
import employeeService from '../../services/employeeService';
import { describeValidationError } from '../../utils/apiErrors';
import masterService from '../../services/masterService';
import { useAuth } from '../../context/AuthContext';
import CommonPagination from '../../components/common/CommonPagination';
import { usePagination } from '../../hooks/usePagination';
import './DoctorSignatureMasterPage.css';

const DEFAULT_QUALIFICATIONS = [
  'MBBS',
  'MD (Pathology)',
  'MD (Microbiology)',
  'MD (Biochemistry)',
  'MD (Radiology)',
  'MD (General Medicine)',
  'DNB (Pathology)',
  'DNB (Radiology)',
  'DNB (Biochemistry)',
  'DNB (Microbiology)',
  'PhD (Medical Sciences)',
  'DCP (Clinical Pathology)',
  'DMRD (Radiodiagnosis)',
  'Diploma in Clinical Pathology',
  'DMLT',
  'BSc MLT',
  'MSc MLT',
  'FICP',
  'FRCP'
];

const FALLBACK_DESIGNATIONS = [
  'Consultant Pathologist',
  'Consultant Pathologist & Lab Director',
  'Consultant Radiologist',
  'Consultant Microbiologist',
  'Consultant Biochemist',
  'Lab Director',
  'Senior Consultant Pathologist',
  'Quality Manager & Pathologist',
  'Pathologist',
  'Radiologist',
  'Doctor',
  'Consultant',
  'Medical Officer',
  'Lab Technologist'
];

// A report prints the doctor's name, qualification, registration number and council. They come from Employee Master only.
const missingMasterFields = (f) => {
  const gaps = [];
  if (!(f.doctorName || '').trim()) gaps.push('name');
  if (!(f.qualification || '').trim()) gaps.push('qualification');
  if (!(f.medicalRegNo || '').trim()) gaps.push('medical registration number');
  if (!(f.regAuthority || '').trim()) gaps.push('registering medical council');
  return gaps;
};

const INITIAL_PROFILE = {
  profileId: 0,
  employeeId: '',
  doctorName: '',
  qualification: '',
  specialization: '',
  medicalRegNo: '',
  regAuthority: 'Delhi Medical Council',
  designation: 'Consultant Pathologist',
  centreId: null,
  departmentId: null,
  status: 'ACTIVE',
  effectiveFrom: new Date().toISOString().split('T')[0],
  effectiveTo: null,
  canApprovePathology: true,
  canApproveMicrobiology: true,
  canApproveRadiology: false
};

export const DoctorSignatureMasterPage = () => {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole('ADMINISTRATOR') : (user?.roleName?.toUpperCase() === 'ADMINISTRATOR' || user?.roleId === 1);

  // Navigation View Mode: 'list' (Doctor Grid) | 'form' (Signature & Profile Configuration)
  const [viewMode, setViewMode] = useState('list');

  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const autoSelectedRef = useRef(false);
  const [employees, setEmployees] = useState([]);
  const [centres, setCentres] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);
  const [originalDoctorName, setOriginalDoctorName] = useState('');

  // Medical Qualification Master State
  const [qualificationsMaster, setQualificationsMaster] = useState(() => {
    try {
      const saved = localStorage.getItem('lis_medical_qualifications_master');
      return saved ? JSON.parse(saved) : DEFAULT_QUALIFICATIONS;
    } catch (e) {
      return DEFAULT_QUALIFICATIONS;
    }
  });
  const [selectedQualifications, setSelectedQualifications] = useState([]);
  const [showAddQualModal, setShowAddQualModal] = useState(false);
  const [newQualInput, setNewQualInput] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [toast, setToast] = useState(null);

  // Form State
  const [formData, setFormData] = useState(INITIAL_PROFILE);
  const [history, setHistory] = useState([]);

  // Delegated Technician Approval State
  const [technicians, setTechnicians] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [techSaving, setTechSaving] = useState(false);
  const [newTech, setNewTech] = useState({
    technicianEmployeeId: '',
    departmentScope: 'ALL',
    approvalLevel: 'APPROVE_VALIDATE',
    allowAutoSignature: true
  });

  // Guided Stepper State (5-Stage Sequential Workflow)
  const [currentStep, setCurrentStep] = useState(1);
  const [flowMode, setFlowMode] = useState('stepper'); // 'stepper' | 'all'

  // Image Upload / Preview State
  const [uploadedBase64, setUploadedBase64] = useState(null);
  const [uploadChangeReason, setUploadChangeReason] = useState('');
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Both dates are optional-looking inputs but effective-from is required; effective-to may be empty (no end date).
  const validateEffectiveDates = (from, to) => {
    if (!from) return 'Effective from date is required.';
    if (Number.isNaN(new Date(from).getTime())) return 'Effective from is not a valid date.';
    if (to) {
      if (Number.isNaN(new Date(to).getTime())) return 'Effective to is not a valid date.';
      if (new Date(to) < new Date(from)) return 'Effective to cannot be before Effective from.';
    }
    return null;
  };

  const showToastMsg = (text, type = 'success') => {
    setToast({ text, type });
    // Errors stay longer: a validation message has to be read, not just noticed.
    setTimeout(() => setToast(null), type === 'error' ? 9000 : 4000);
  };

  const loadTechnicians = useCallback(async (profileId) => {
    if (!profileId) {
      setTechnicians([]);
      return;
    }
    try {
      setTechLoading(true);
      const res = await doctorSignatureService.getTechnicianMappings(profileId);
      setTechnicians(res.data || []);
    } catch (err) {
      console.error('Failed to load technician mappings:', err);
      setTechnicians([]);
    } finally {
      setTechLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [profilesRes, empRes] = await Promise.all([
        doctorSignatureService.getAllProfiles(),
        employeeService.getEmployees({ pageIndex: 1, pageSize: 200 })
      ]);

      const profList = profilesRes.data || [];
      setProfiles(profList);

      if (empRes?.data?.items) {
        setEmployees(empRes.data.items);
      }

      // Load Centers (from Employee lookup or Center Master)
      try {
        const cRes = await employeeService.getCentresLookup();
        setCentres(cRes.data || []);
      } catch (e) {
        try {
          const cRes2 = await masterService.getCentres();
          setCentres(cRes2.data || []);
        } catch (e2) {}
      }

      // Load Designations from Designation Master
      try {
        const dRes = await employeeService.getDesignationsLookup();
        const dList = (dRes.data || [])
          .map(d => typeof d === 'string' ? d : (d.name || d.designationName || d.designation))
          .filter(Boolean);
        if (dList.length > 0) {
          setDesignationsList(dList);
        } else {
          setDesignationsList(FALLBACK_DESIGNATIONS);
        }
      } catch (e) {
        setDesignationsList(FALLBACK_DESIGNATIONS);
      }

      // Pick the first doctor only on the very first load. Re-running this on every change of selectedProfile
      // re-selected the first doctor right after "+ Add Reporting Doctor" had cleared the form.
      if (profList.length > 0 && !autoSelectedRef.current) {
        autoSelectedRef.current = true;
        handleSelectProfile(profList[0], false);
      }
    } catch (err) {
      showToastMsg('Failed to load doctor signature profiles.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Center Permissions: Admin sees all, Normal employee sees assigned
  const userCentres = useMemo(() => {
    if (isAdmin) return centres;
    if (!user) return centres;
    const allowedIds = new Set();
    if (user.centreId) allowedIds.add(user.centreId);
    if (user.workspaces && Array.isArray(user.workspaces)) {
      user.workspaces.forEach(w => allowedIds.add(w.centreId));
    }
    if (user.centres && Array.isArray(user.centres)) {
      user.centres.forEach(c => allowedIds.add(c.centreId || c));
    }
    if (allowedIds.size === 0) return centres;
    return centres.filter(c => allowedIds.has(c.centreId || c.id));
  }, [centres, isAdmin, user]);

  // Step 1 lists the DOCTOR accounts of the selected centre (Pathologist / Radiologist roles or a doctor designation).
  // The profile belongs to the doctor who logs in and approves; technicians are authorised separately in Step 4.
  // The account already linked to the profile is always kept in the list so an existing profile never shows a blank.
  const centerDoctorAccounts = useMemo(() => {
    if (!formData.centreId) return [];
    const cId = parseInt(formData.centreId, 10);
    const DOCTOR_WORDS = ['pathologist', 'radiologist', 'microbiologist', 'doctor', 'consultant', 'physician'];
    const hasDoctorWord = (v) => !!v && DOCTOR_WORDS.some((w) => String(v).toLowerCase().includes(w));
    return employees.filter(emp => {
      const matchesCentre =
        emp.centreId === cId ||
        (emp.assignedCentres && emp.assignedCentres.includes(cId)) ||
        (emp.workspaces && emp.workspaces.some(w => w.centreId === cId));

      const isDoctorAccount =
        emp.roleId === 5 || emp.roleId === 6 ||
        hasDoctorWord(emp.roleName) || hasDoctorWord(emp.designation) || hasDoctorWord(emp.category);

      const isCurrentlyLinked = !!formData.employeeId && emp.employeeId === parseInt(formData.employeeId, 10);
      return isCurrentlyLinked || (matchesCentre && isDoctorAccount);
    });
  }, [employees, formData.centreId, formData.employeeId]);

  const handleSelectProfile = async (profile, switchView = true) => {
    setSelectedProfile(profile);
    setOriginalDoctorName(profile.doctorName || '');

    // Parse qualifications into array of tags
    const qualStr = profile.qualification || '';
    const qArr = qualStr.split(',').map(s => s.trim()).filter(Boolean);
    setSelectedQualifications(qArr);

    setFormData({
      profileId: profile.profileId,
      employeeId: profile.employeeId,
      doctorName: profile.doctorName,
      qualification: profile.qualification,
      specialization: profile.specialization || '',
      medicalRegNo: profile.medicalRegNo,
      regAuthority: profile.regAuthority || 'Delhi Medical Council',
      designation: profile.designation || 'Consultant Pathologist',
      centreId: profile.centreId || null,
      departmentId: profile.departmentId || null,
      status: profile.status,
      effectiveFrom: profile.effectiveFrom ? profile.effectiveFrom.split('T')[0] : '',
      effectiveTo: profile.effectiveTo ? profile.effectiveTo.split('T')[0] : '',
      canApprovePathology: profile.canApprovePathology,
      canApproveMicrobiology: profile.canApproveMicrobiology,
      canApproveRadiology: profile.canApproveRadiology
    });
    setUploadedBase64(null);
    setUploadChangeReason('');

    try {
      const histRes = await doctorSignatureService.getProfileHistory(profile.profileId);
      setHistory(histRes.data || []);
    } catch (e) {
      setHistory([]);
    }

    loadTechnicians(profile.profileId);
    setCurrentStep(1);

    if (switchView) {
      setViewMode('form');
    }
  };

  const handleNewProfile = () => {
    setSelectedProfile(null);
    setOriginalDoctorName('');
    setSelectedQualifications([]);
    const defaultCentre = userCentres.length > 0 ? (userCentres[0].centreId || userCentres[0].id) : null;
    setFormData({
      ...INITIAL_PROFILE,
      centreId: defaultCentre
    });
    setUploadedBase64(null);
    setUploadChangeReason('');
    setHistory([]);
    setTechnicians([]);
    setCurrentStep(1);
    setNewTech({
      technicianEmployeeId: '',
      departmentScope: 'ALL',
      approvalLevel: 'APPROVE_VALIDATE',
      allowAutoSignature: true
    });
    setViewMode('form');
  };

  // Center Selection Handler
  const handleCenterChange = (e) => {
    const newCentreId = e.target.value ? parseInt(e.target.value, 10) : null;
    setFormData(prev => ({
      ...prev,
      centreId: newCentreId,
      employeeId: '' // Clear technician when center changes
    }));
    setOriginalDoctorName('');
  };

  // Technician Employee Selection Handler
  const handleTechnicianEmployeeChange = (e) => {
    const empId = parseInt(e.target.value, 10);
    const emp = employees.find(x => x.employeeId === empId);
    if (emp) {
      const name = (emp.name || '').trim();
      const display = /^dr[. ]/i.test(name) ? name : `Dr. ${name}`;
      setOriginalDoctorName(display);
      setSelectedQualifications((emp.qualification || '').split(',').map(q => q.trim()).filter(Boolean));
      setFormData(prev => ({
        ...prev,
        employeeId: empId,
        doctorName: display,
        qualification: emp.qualification || '',
        designation: emp.designation || '',
        medicalRegNo: emp.medicalRegNo || '',
        regAuthority: emp.regAuthority || ''
      }));
    } else {
      setOriginalDoctorName('');
      setSelectedQualifications([]);
      setFormData(prev => ({ ...prev, employeeId: '', doctorName: '', qualification: '', designation: '', medicalRegNo: '', regAuthority: '' }));
    }
  };

  // Qualification Master Selection & Add New Degree Handlers
  const handleAddQualification = (deg) => {
    if (!deg) return;
    if (!selectedQualifications.includes(deg)) {
      const updated = [...selectedQualifications, deg];
      setSelectedQualifications(updated);
      setFormData(prev => ({ ...prev, qualification: updated.join(', ') }));
    }
  };

  const handleRemoveQualification = (deg) => {
    const updated = selectedQualifications.filter(x => x !== deg);
    setSelectedQualifications(updated);
    setFormData(prev => ({ ...prev, qualification: updated.join(', ') }));
  };

  const handleCreateNewQualification = () => {
    const trimmed = newQualInput.trim();
    if (!trimmed) return;
    if (!qualificationsMaster.includes(trimmed)) {
      const updatedMaster = [...qualificationsMaster, trimmed];
      setQualificationsMaster(updatedMaster);
      try {
        localStorage.setItem('lis_medical_qualifications_master', JSON.stringify(updatedMaster));
      } catch (e) {}
    }
    handleAddQualification(trimmed);
    setNewQualInput('');
    setShowAddQualModal(false);
    showToastMsg(`Added "${trimmed}" to Medical Qualification Master!`, 'success');
  };

  // Step Validation & Navigation
  const canGoToStep = (targetStep) => {
    if (targetStep > 1) {
      if (!formData.centreId) {
        showToastMsg('Please select a Center in Step 1.', 'error');
        return false;
      }
      if (!formData.employeeId) {
        showToastMsg("Please select the doctor's staff account in Step 1.", 'error');
        return false;
      }
      const gaps = missingMasterFields(formData);
      if (gaps.length) {
        showToastMsg(`Missing in Employee Master: ${gaps.join(', ')}. Complete it there, then tag the doctor here.`, 'error');
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (canGoToStep(currentStep + 1)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = (step) => {
    if (canGoToStep(step)) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveAndUnlockDelegation = async (e) => {
    if (e) e.preventDefault();
    if (!canGoToStep(2)) return;
    const dateProblem = validateEffectiveDates(formData.effectiveFrom, formData.effectiveTo);
    if (dateProblem) {
      showToastMsg(dateProblem, 'error');
      return;
    }
    try {
      setSaving(true);
      if (!formData.employeeId) {
        showToastMsg('Please select a linked employee profile in Step 1.', 'error');
        setCurrentStep(1);
        setSaving(false);
        return;
      }
      const createRes = await doctorSignatureService.createProfile({
        employeeId: parseInt(formData.employeeId, 10),
        doctorName: formData.doctorName,
        qualification: formData.qualification,
        specialization: formData.specialization,
        medicalRegNo: formData.medicalRegNo,
        regAuthority: formData.regAuthority,
        designation: formData.designation,
        centreId: formData.centreId,
        departmentId: formData.departmentId,
        signatureBase64: uploadedBase64 || '',
        effectiveFrom: formData.effectiveFrom,
        canApprovePathology: formData.canApprovePathology,
        canApproveMicrobiology: formData.canApproveMicrobiology,
        canApproveRadiology: formData.canApproveRadiology
      });
      showToastMsg(`Doctor profile registered successfully! You can now authorize technicians.`, 'success');
      const newProf = createRes.data;
      setSelectedProfile(newProf);
      setFormData(prev => ({ ...prev, profileId: newProf.profileId }));
      await loadTechnicians(newProf.profileId);
      const profilesRes = await doctorSignatureService.getAllProfiles();
      setProfiles(profilesRes.data || []);
      setCurrentStep(4);
    } catch (err) {
      showToastMsg(describeValidationError(err, 'Failed to create doctor profile.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTechnician = async (e) => {
    e.preventDefault();
    if (!selectedProfile?.profileId) return;

    if (!newTech.technicianEmployeeId) {
      showToastMsg('Please select a technician from the employee list.', 'error');
      return;
    }

    try {
      setTechSaving(true);
      await doctorSignatureService.addTechnicianMapping(selectedProfile.profileId, {
        technicianEmployeeId: parseInt(newTech.technicianEmployeeId, 10),
        departmentScope: newTech.departmentScope,
        approvalLevel: newTech.approvalLevel,
        allowAutoSignature: newTech.allowAutoSignature
      });

      showToastMsg('Technician mapped and authorized successfully!', 'success');
      setNewTech({
        technicianEmployeeId: '',
        departmentScope: 'ALL',
        approvalLevel: 'APPROVE_VALIDATE',
        allowAutoSignature: true
      });
      await loadTechnicians(selectedProfile.profileId);
      const profilesRes = await doctorSignatureService.getAllProfiles();
      setProfiles(profilesRes.data || []);
    } catch (err) {
      showToastMsg(err.response?.data?.message || 'Failed to authorize technician.', 'error');
    } finally {
      setTechSaving(false);
    }
  };

  const handleToggleTechStatus = async (mappingId, currentStatus) => {
    if (!selectedProfile?.profileId) return;
    try {
      await doctorSignatureService.toggleTechnicianStatus(selectedProfile.profileId, mappingId, !currentStatus);
      showToastMsg(`Technician status ${!currentStatus ? 'activated' : 'deactivated'}.`, 'success');
      await loadTechnicians(selectedProfile.profileId);
    } catch (err) {
      showToastMsg('Failed to update technician status.', 'error');
    }
  };

  const handleDeleteTechMapping = async (mappingId, techName) => {
    if (!selectedProfile?.profileId) return;
    if (!window.confirm(`Are you sure you want to revoke approval authority for "${techName}" under this doctor?`)) {
      return;
    }

    try {
      await doctorSignatureService.deleteTechnicianMapping(selectedProfile.profileId, mappingId);
      showToastMsg(`Authority for ${techName} revoked successfully.`, 'success');
      await loadTechnicians(selectedProfile.profileId);
      const profilesRes = await doctorSignatureService.getAllProfiles();
      setProfiles(profilesRes.data || []);
    } catch (err) {
      showToastMsg('Failed to revoke technician mapping.', 'error');
    }
  };

  const handleEmployeeChange = (e) => {
    const empId = parseInt(e.target.value, 10);
    const emp = employees.find(x => x.employeeId === empId);
    if (emp) {
      const docPrefix = emp.name.toLowerCase().startsWith('dr.') ? '' : 'Dr. ';
      setFormData(prev => ({
        ...prev,
        employeeId: empId,
        doctorName: `${docPrefix}${emp.name}`.trim(),
        designation: emp.designation || 'Consultant Pathologist'
      }));
    } else {
      setFormData(prev => ({ ...prev, employeeId: '' }));
    }
  };

  // Automated Whitespace Trimming & Auto-Crop on Canvas
  const processSignatureImage = (file) => {
    if (!file) return;

    if (!file.type.match(/^image\/(png|jpeg|jpg|webp)$/i)) {
      showToastMsg('Please upload a valid image file (PNG, JPG, WEBP).', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToastMsg('Signature file size must not exceed 2MB.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current || document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const { data, width, height } = imgData;

        let minX = width, minY = height, maxX = 0, maxY = 0;
        let found = false;

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const a = data[idx + 3];

            const isWhiteOrTransparent = a < 20 || (r > 240 && g > 240 && b > 240);

            if (!isWhiteOrTransparent) {
              found = true;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (!found) {
          showToastMsg('No signature content detected in the uploaded file.', 'error');
          return;
        }

        const padding = 15;
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = Math.min(width, maxX + padding);
        maxY = Math.min(height, maxY + padding);

        const cropW = maxX - minX;
        const cropH = maxY - minY;

        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = cropW;
        croppedCanvas.height = cropH;
        const cropCtx = croppedCanvas.getContext('2d');

        cropCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

        const croppedBase64 = croppedCanvas.toDataURL('image/png');
        setUploadedBase64(croppedBase64);
        showToastMsg('Signature successfully cropped & optimized with high precision.', 'success');
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processSignatureImage(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processSignatureImage(e.target.files[0]);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (!formData.doctorName.trim()) {
      showToastMsg('Doctor name is required.', 'error');
      return;
    }
    if (!formData.qualification.trim()) {
      showToastMsg('Medical qualifications are required.', 'error');
      return;
    }
    if (!formData.medicalRegNo.trim()) {
      showToastMsg('Medical registration number (DMC/MCI/NMC) is required.', 'error');
      return;
    }
    if (!(formData.regAuthority || '').trim()) {
      showToastMsg('Medical registration authority (for example Delhi Medical Council) is required.', 'error');
      return;
    }
    const dateProblem = validateEffectiveDates(formData.effectiveFrom, formData.effectiveTo);
    if (dateProblem) {
      showToastMsg(dateProblem, 'error');
      return;
    }

    try {
      setSaving(true);

      if (selectedProfile && selectedProfile.profileId > 0) {
        await doctorSignatureService.updateProfile(selectedProfile.profileId, {
          doctorName: formData.doctorName.trim(), // the API requires it on update as well
          qualification: formData.qualification,
          specialization: formData.specialization,
          medicalRegNo: formData.medicalRegNo,
          regAuthority: formData.regAuthority,
          designation: formData.designation,
          centreId: formData.centreId,
          departmentId: formData.departmentId,
          status: formData.status,
          effectiveFrom: formData.effectiveFrom,
          effectiveTo: formData.effectiveTo || null, // an empty date input is "" and the API rejects "" as a date
          canApprovePathology: formData.canApprovePathology,
          canApproveMicrobiology: formData.canApproveMicrobiology,
          canApproveRadiology: formData.canApproveRadiology
        });

        if (uploadedBase64) {
          const uploadRes = await doctorSignatureService.uploadSignatureImage(selectedProfile.profileId, {
            signatureBase64: uploadedBase64,
            changeReason: uploadChangeReason || 'Updated official doctor signature'
          });
          showToastMsg(`Profile updated & new signature registered as Version ${uploadRes.data}! Previous versions retained for historical audit.`, 'success');
        } else {
          showToastMsg('Doctor profile details saved successfully.', 'success');
        }
      } else {
        if (!formData.employeeId) {
          showToastMsg('Please select a linked employee profile.', 'error');
          setSaving(false);
          return;
        }

        const createRes = await doctorSignatureService.createProfile({
          employeeId: parseInt(formData.employeeId, 10),
          doctorName: formData.doctorName,
          qualification: formData.qualification,
          specialization: formData.specialization,
          medicalRegNo: formData.medicalRegNo,
          regAuthority: formData.regAuthority,
          designation: formData.designation,
          centreId: formData.centreId,
          departmentId: formData.departmentId,
          signatureBase64: uploadedBase64 || '',
          effectiveFrom: formData.effectiveFrom,
          canApprovePathology: formData.canApprovePathology,
          canApproveMicrobiology: formData.canApproveMicrobiology,
          canApproveRadiology: formData.canApproveRadiology
        });

        showToastMsg(`Doctor profile registered successfully for ${createRes.data?.doctorName || 'doctor'}!`, 'success');
      }

      await loadData();
      setViewMode('list');
    } catch (err) {
      showToastMsg(describeValidationError(err, 'Failed to save doctor profile.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedProfile) return;
    const newStatus = selectedProfile.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await doctorSignatureService.updateStatus(selectedProfile.profileId, newStatus);
      showToastMsg(`Doctor profile status changed to ${newStatus}.`, 'success');
      await loadData();
    } catch (e) {
      showToastMsg(describeValidationError(e, 'Failed to change profile status.'), 'error');
    }
  };

  // Filter Profiles
  const filteredProfiles = useMemo(() => {
    return profiles.filter((p) => {
      const q = searchTerm.toLowerCase().trim();
      const matchSearch =
        !q ||
        (p.doctorName && p.doctorName.toLowerCase().includes(q)) ||
        (p.medicalRegNo && p.medicalRegNo.toLowerCase().includes(q)) ||
        (p.qualification && p.qualification.toLowerCase().includes(q)) ||
        (p.designation && p.designation.toLowerCase().includes(q)) ||
        (p.regAuthority && p.regAuthority.toLowerCase().includes(q));

      const matchDept =
        deptFilter === 'ALL' ||
        (deptFilter === 'PATH' && p.canApprovePathology) ||
        (deptFilter === 'MICRO' && p.canApproveMicrobiology) ||
        (deptFilter === 'RADIO' && p.canApproveRadiology);

      const matchStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && p.status === 'ACTIVE') ||
        (statusFilter === 'INACTIVE' && p.status !== 'ACTIVE');

      return matchSearch && matchDept && matchStatus;
    });
  }, [profiles, searchTerm, deptFilter, statusFilter]);

  // Pagination for Doctor Grid
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    pagedItems,
    totalPages,
    totalRecords
  } = usePagination({
    items: filteredProfiles,
    initialPageSize: 10,
    resetDeps: [searchTerm, deptFilter, statusFilter]
  });

  return (
    <div className="doc-sig-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`doc-sig-toast ${toast.type}`}>
          {toast.text}
        </div>
      )}

      {/* Hidden processing canvas */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileInputChange}
      />

      {/* Main Header */}
      <div className="doc-sig-header">
        <div className="doc-sig-title-block">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1>
              {viewMode === 'list'
                ? 'Reporting Doctors & Authorized Signatories Catalog'
                : 'Doctor Signature & Authority Configuration'}
            </h1>
            {viewMode === 'form' && selectedProfile && (
              <span className="badge-active" style={{ fontSize: '0.8rem', padding: '3px 10px' }}>
                Editing: {selectedProfile.doctorName} ({selectedProfile.medicalRegNo})
              </span>
            )}
          </div>
          <p className="doc-sig-subtitle">
            {viewMode === 'list'
              ? 'Centralized doctor directory, medical council registration tracking (DMC/MCI/NMC), and active signature profiles.'
              : 'Medical council registration tracking, transparent signature image QC, and immutable versioning for clinical reports.'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {viewMode === 'form' ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setViewMode('list')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>View Doctor List</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                if (selectedProfile) setViewMode('form');
                else if (profiles.length > 0) handleSelectProfile(profiles[0], true);
                else handleNewProfile();
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span>Signature Configuration</span>
            </button>
          )}

          <button
            type="button"
            className="btn-primary"
            onClick={handleNewProfile}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>+ Add Reporting Doctor</span>
          </button>

          <button
            type="button"
            className="btn-secondary"
            onClick={loadData}
            title="Refresh catalog"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VIEW MODE 1: DEDICATED DOCTOR LIST / GRID PAGE                            */}
      {/* ========================================================================= */}
      {viewMode === 'list' && (
        <div className="doc-grid-card">
          {/* Filter Bar */}
          <div className="doc-grid-filter-bar">
            <div className="doc-grid-search-wrap">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by doctor name, qualification, registration no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                >
                  ✕
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <select
                className="doc-grid-select"
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
              >
                <option value="ALL">All Departments</option>
                <option value="PATH">Pathology & Biochemistry</option>
                <option value="MICRO">Microbiology & Serology</option>
                <option value="RADIO">Radiology & Imaging</option>
              </select>

              <select
                className="doc-grid-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active Only</option>
                <option value="INACTIVE">Inactive Only</option>
              </select>
            </div>

            <div style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
              Showing {filteredProfiles.length} Doctors
            </div>
          </div>

          {/* Doctor Details Grid / Table */}
          <div className="table-responsive" style={{ overflowX: 'auto' }}>
            <table className="doc-master-table">
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                  <th>Doctor Name & Designation</th>
                  <th>Medical Qualification</th>
                  <th>Medical Registration No.</th>
                  <th>Registration Authority</th>
                  <th>Reporting Authority Scopes</th>
                  <th style={{ textAlign: 'center' }}>Authorized Technicians</th>
                  <th style={{ textAlign: 'center' }}>Signature Version</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '160px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Loading doctor registry...
                    </td>
                  </tr>
                ) : filteredProfiles.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      No doctor records found matching your filters.
                    </td>
                  </tr>
                ) : (
                  pagedItems.map((doc, index) => {
                    const srNo = (page - 1) * pageSize + index + 1;
                    return (
                      <tr key={doc.profileId}>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace', color: '#64748b' }}>
                          {srNo}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                              {doc.doctorName}
                            </span>
                            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                              {doc.designation || 'Reporting Medical Officer'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.86rem' }}>
                            {doc.qualification}
                          </span>
                        </td>
                        <td>
                          <span className="doc-reg-pill">
                            {doc.medicalRegNo}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: '#475569' }}>
                          {doc.regAuthority || 'State Medical Council'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {doc.canApprovePathology && <span className="badge-dept">Pathology</span>}
                            {doc.canApproveMicrobiology && <span className="badge-dept">Microbiology</span>}
                            {doc.canApproveRadiology && <span className="badge-dept">Radiology</span>}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge-tech-count" title={`${doc.authorizedTechnicianCount || 0} technicians authorized`}>
                            👥 {doc.authorizedTechnicianCount || 0} Technicians
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <img
                              src={doctorSignatureService.getSignatureImageUrl(doc.profileId)}
                              alt="Signature"
                              className="doc-table-sig-preview"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span className="badge-version">v{doc.currentVersion}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={doc.status === 'ACTIVE' ? 'badge-active' : 'badge-inactive'}>
                            {doc.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn-action-primary"
                            onClick={() => handleSelectProfile(doc, true)}
                            title="Configure signature and medical credentials"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            <span>Configure</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredProfiles.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
              <CommonPagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW MODE 2: DOCTOR SIGNATURE & PROFILE FULL-WIDTH EDITOR (NO SIDEBAR)     */}
      {/* ========================================================================= */}
      {viewMode === 'form' && (
        <div className="doc-sig-form-full">
          {/* Top Bar with View Doctor Action & Flow Switcher */}
          <div className="doc-form-top-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setViewMode('list')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>Back to Doctor List</span>
            </button>

            {selectedProfile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Doctor ID: #{selectedProfile.profileId}</span>
                <span className="doc-reg-pill">{selectedProfile.medicalRegNo}</span>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>{selectedProfile.doctorName}</span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="badge-active" style={{ fontSize: '0.8rem', padding: '3px 10px', background: '#e0f2fe', color: '#0369a1' }}>
                  + New Reporting Doctor Registration
                </span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
              {/* Flow Mode Switcher: Step-by-Step Flow vs All-in-One */}
              <div className="flow-toggle-switch">
                <button
                  type="button"
                  className={`flow-toggle-btn ${flowMode === 'stepper' ? 'active' : ''}`}
                  onClick={() => setFlowMode('stepper')}
                  title="Guided step-by-step wizard (Primary Stage to Final Stage)"
                >
                  🚶 Step-by-Step Flow
                </button>
                <button
                  type="button"
                  className={`flow-toggle-btn ${flowMode === 'all' ? 'active' : ''}`}
                  onClick={() => setFlowMode('all')}
                  title="View all configuration sections together"
                >
                  📋 All-in-One View
                </button>
              </div>

              {selectedProfile && (
                <button
                  type="button"
                  onClick={handleToggleStatus}
                  style={{
                    fontSize: '0.75rem',
                    padding: '5px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: 'none',
                    background: selectedProfile.status === 'ACTIVE' ? '#fee2e2' : '#dcfce7',
                    color: selectedProfile.status === 'ACTIVE' ? '#991b1b' : '#166534',
                    fontWeight: '700'
                  }}
                >
                  {selectedProfile.status === 'ACTIVE' ? 'Deactivate Doctor' : 'Activate Doctor'}
                </button>
              )}
            </div>
          </div>

          {/* ========================================================================= */}
          {/* STEPPER PROGRESS HEADER (VISUAL STEP-BY-STEP PROGRESS)                     */}
          {/* ========================================================================= */}
          {flowMode === 'stepper' && (
            <div className="doc-sig-stepper-container">
              <div className="doc-sig-stepper-header">
                {/* Step 1 */}
                <button
                  type="button"
                  className={`stepper-step-btn ${currentStep === 1 ? 'active' : ''} ${
                    formData.doctorName && formData.qualification && formData.medicalRegNo && currentStep !== 1 ? 'completed' : ''
                  }`}
                  onClick={() => handleStepClick(1)}
                >
                  <div className="step-circle">
                    {formData.doctorName && formData.qualification && formData.medicalRegNo && currentStep !== 1 ? '✓' : '1'}
                  </div>
                  <div className="step-labels">
                    <span className="step-main-title">1. Doctor Details</span>
                    <span className="step-sub-title">नाम व रजिस्ट्रेशन</span>
                  </div>
                </button>

                {/* Step 2 */}
                <button
                  type="button"
                  className={`stepper-step-btn ${currentStep === 2 ? 'active' : ''} ${
                    (formData.canApprovePathology || formData.canApproveMicrobiology || formData.canApproveRadiology) && currentStep > 2 ? 'completed' : ''
                  }`}
                  onClick={() => handleStepClick(2)}
                >
                  <div className="step-circle">
                    {(formData.canApprovePathology || formData.canApproveMicrobiology || formData.canApproveRadiology) && currentStep > 2 ? '✓' : '2'}
                  </div>
                  <div className="step-labels">
                    <span className="step-main-title">2. Department Rights</span>
                    <span className="step-sub-title">डिपार्टमेंट अधिकार</span>
                  </div>
                </button>

                {/* Step 3 */}
                <button
                  type="button"
                  className={`stepper-step-btn ${currentStep === 3 ? 'active' : ''} ${
                    (uploadedBase64 || selectedProfile?.hasSignature) && currentStep > 3 ? 'completed' : ''
                  }`}
                  onClick={() => handleStepClick(3)}
                >
                  <div className="step-circle">
                    {(uploadedBase64 || selectedProfile?.hasSignature) && currentStep > 3 ? '✓' : '3'}
                  </div>
                  <div className="step-labels">
                    <span className="step-main-title">3. Digital Signature</span>
                    <span className="step-sub-title">हस्ताक्षर व प्रिव्यू</span>
                  </div>
                </button>

                {/* Step 4 */}
                <button
                  type="button"
                  className={`stepper-step-btn ${currentStep === 4 ? 'active' : ''} ${
                    technicians.length > 0 && currentStep > 4 ? 'completed' : ''
                  }`}
                  onClick={() => handleStepClick(4)}
                >
                  <div className="step-circle">
                    {technicians.length > 0 && currentStep > 4 ? '✓' : '4'}
                  </div>
                  <div className="step-labels">
                    <span className="step-main-title">4. Lab Technicians</span>
                    <span className="step-sub-title">स्टाफ अधिकार मैपिंग</span>
                  </div>
                </button>

                {/* Step 5 */}
                <button
                  type="button"
                  className={`stepper-step-btn ${currentStep === 5 ? 'active' : ''}`}
                  onClick={() => handleStepClick(5)}
                >
                  <div className="step-circle">5</div>
                  <div className="step-labels">
                    <span className="step-main-title">5. Review & Save</span>
                    <span className="step-sub-title">फाइनल समीक्षा व सेव</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSaveProfile}>
            {/* ===================================================================== */}
            {/* STAGE 1: DOCTOR IDENTIFICATION & MEDICAL COUNCIL REGISTRATION         */}
            {/* ===================================================================== */}
            {(flowMode === 'all' || currentStep === 1) && (
              <div className="doc-sig-section">
                <div className="step-guide-box">
                  <div className="step-guide-icon">📋</div>
                  <div className="step-guide-text">
                    <h3>Step 1: Doctor Identity & Medical Registration (डॉक्टर की पहचान व रजिस्ट्रेशन)</h3>
                    <p>
                      Yahan doctor ki basic clinical details bharein. Medical Registration Number (DMC/NMC/MCI) aur Qualifications patient ki har report ke signature footer me legally print hoti hain.
                    </p>
                  </div>
                </div>

                <div className="doc-sig-section-title">
                  <span>Step 1: Doctor Identity & Medical Registration</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#64748b' }}>
                    * Mandatory Master-Driven Configuration
                  </span>
                </div>

                {/* 3-Card Responsive Grouping */}
                <div className="step1-cards-grid">
                  {/* CARD 1 — Center & Staff Assignment */}
                  <div className="doc-step1-card card-center-staff">
                    <div className="step1-card-header">
                      <div className="step1-card-icon">🏥</div>
                      <div>
                        <h4>CARD 1 — Center & Staff Assignment</h4>
                        <span className="step1-card-desc">Center-wise technician account link</span>
                      </div>
                    </div>
                    <div className="step1-card-body">
                      <div className="doc-sig-field">
                        <label>Center *</label>
                        <select
                          value={formData.centreId || ''}
                          onChange={handleCenterChange}
                          required
                        >
                          <option value="">-- Select Center --</option>
                          {userCentres.map(c => {
                            const cId = c.centreId || c.id;
                            const cName = c.centreName || c.name || `Center #${cId}`;
                            return (
                              <option key={cId} value={cId}>
                                {cName}
                              </option>
                            );
                          })}
                        </select>
                        <small className="field-hint">
                          {isAdmin
                            ? 'Administrator view: Select from all lab centers'
                            : 'Normal employee view: Showing centers assigned to you'}
                        </small>
                      </div>

                      <div className="doc-sig-field" style={{ marginTop: '16px' }}>
                        <label>Doctor's Staff Account (whose signature profile this is) *</label>
                        <select
                          value={formData.employeeId || ''}
                          onChange={handleTechnicianEmployeeChange}
                          disabled={!formData.centreId || !!selectedProfile}
                          required
                        >
                          <option value="">
                            {!formData.centreId
                              ? '-- First Select Center Above --'
                              : '-- Select Doctor / Staff Account --'}
                          </option>
                          {centerDoctorAccounts
                            .filter(emp => !selectedProfile || String(emp.employeeId) === String(formData.employeeId))
                            .map(emp => (
                              <option key={emp.employeeId} value={emp.employeeId}>
                                {emp.title && !/^dr[. ]/i.test((emp.name || '').trim()) ? `${emp.title} ` : ''}{emp.name} ({emp.designation || emp.roleName || 'Doctor'})
                              </option>
                            ))}
                        </select>
                        {selectedProfile && (
                          <small className="field-hint">
                            Existing profile: the linked doctor account cannot be changed. To use another doctor, add a new reporting doctor.
                          </small>
                        )}
                        <small className="field-hint">
                          This links the profile to the doctor's own employee record. The doctor name and qualifications below are filled from it. (Technicians who work on the doctor's behalf are added later, in Step 4.)
                        </small>

                        {formData.centreId && centerDoctorAccounts.length === 0 && (
                          <div className="no-technician-alert">
                            <span>⚠️ No doctor (Pathologist / Radiologist) account is assigned to this Center.</span>
                            <a
                              href="/employees"
                              target="_blank"
                              rel="noreferrer"
                              className="btn-create-tech-link"
                            >
                              + Create Doctor in Employee Master ↗
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* CARD 2 — printed on the report: read-only, from Employee Master */}
                  <div className="doc-step1-card card-doctor-identity">
                    <div className="step1-card-header">
                      <div className="step1-card-icon">📄</div>
                      <div>
                        <h4>CARD 2 — Printed on the report</h4>
                        <span className="step1-card-desc">Read-only. Comes from Employee Master.</span>
                      </div>
                    </div>
                    <div className="step1-card-body">
                      {!formData.employeeId ? (
                        <small className="field-hint">Select the doctor's staff account in Card 1 to see these details.</small>
                      ) : (
                        <>
                          <dl className="master-readonly-list">
                            <div><dt>Doctor name</dt><dd>{formData.doctorName || '—'}</dd></div>
                            <div><dt>Qualification</dt><dd>{formData.qualification || '—'}</dd></div>
                            <div><dt>Designation</dt><dd>{formData.designation || '—'}</dd></div>
                            <div><dt>Medical registration no.</dt><dd>{formData.medicalRegNo || '—'}</dd></div>
                            <div><dt>Registering council</dt><dd>{formData.regAuthority || '—'}</dd></div>
                          </dl>
                          {missingMasterFields(formData).length > 0 && (
                            <div className="no-technician-alert">
                              <span>⚠️ Missing in Employee Master: {missingMasterFields(formData).join(', ')}.</span>
                              <a href="/employees" target="_blank" rel="noreferrer" className="btn-create-tech-link">Open Employee Master ↗</a>
                            </div>
                          )}
                          <small className="field-hint">To change any of these, edit the employee in Employee Master. This screen only tags.</small>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {flowMode === 'stepper' && (
                  <div className="stepper-nav-footer">
                    <button
                      type="button"
                      className="btn-step-prev"
                      onClick={() => setViewMode('list')}
                    >
                      ✕ Cancel & Return
                    </button>
                    <div className="stepper-progress-pill">Stage 1 of 5: Doctor Profile</div>
                    <button
                      type="button"
                      className="btn-step-next"
                      onClick={handleNextStep}
                    >
                      <span>Continue to Step 2: Department Rights</span>
                      <span>➔</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ===================================================================== */}
            {/* STAGE 2: DEPARTMENT REPORTING RIGHTS & SCOPES                         */}
            {/* ===================================================================== */}
            {(flowMode === 'all' || currentStep === 2) && (
              <div className="doc-sig-section">
                <div className="step-guide-box">
                  <div className="step-guide-icon">🔬</div>
                  <div className="step-guide-text">
                    <h3>Step 2: Department Authority & Scope (डिपार्टमेंट अधिकार - डॉक्टर कौनसे टेस्ट साइन करेगा)</h3>
                    <p>
                      Chunein ki yeh doctor kis-kis laboratory department ke reports sign karne ke liye authorized hain. Kisi bhi card par click karke ON/OFF karein.
                    </p>
                  </div>
                </div>

                <div className="doc-sig-section-title">
                  <span>Reporting & Signature Authority Scopes</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#64748b' }}>
                    Click cards to toggle authority
                  </span>
                </div>

                <div className="dept-cards-grid">
                  {/* Card 1: Pathology */}
                  <div
                    className={`dept-card-interactive ${formData.canApprovePathology ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, canApprovePathology: !prev.canApprovePathology }))}
                  >
                    <div className="dept-card-icon">🔬</div>
                    <div className="dept-card-body">
                      <h4>Pathology & Biochemistry</h4>
                      <p>
                        Blood tests, CBC, Liver Function Tests (LFT), Kidney Function Tests (KFT), Lipid Profile, Urine Examination, and Clinical Biochemistry.
                      </p>
                    </div>
                    <div className="dept-card-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.canApprovePathology}
                        onChange={() => {}}
                      />
                    </div>
                  </div>

                  {/* Card 2: Microbiology */}
                  <div
                    className={`dept-card-interactive ${formData.canApproveMicrobiology ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, canApproveMicrobiology: !prev.canApproveMicrobiology }))}
                  >
                    <div className="dept-card-icon">🧫</div>
                    <div className="dept-card-body">
                      <h4>Microbiology & Serology</h4>
                      <p>
                        Bacterial / Fungal Cultures, Antibiotic Sensitivity (AST), Infectious Diseases, ELISA, Widal, Dengue & Serological assays.
                      </p>
                    </div>
                    <div className="dept-card-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.canApproveMicrobiology}
                        onChange={() => {}}
                      />
                    </div>
                  </div>

                  {/* Card 3: Radiology */}
                  <div
                    className={`dept-card-interactive ${formData.canApproveRadiology ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, canApproveRadiology: !prev.canApproveRadiology }))}
                  >
                    <div className="dept-card-icon">🩻</div>
                    <div className="dept-card-body">
                      <h4>Radiology & Imaging</h4>
                      <p>
                        Digital X-Ray, Ultrasonography (USG), CT Scan, MRI, ECG, and Diagnostic Imaging reporting sign-offs.
                      </p>
                    </div>
                    <div className="dept-card-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.canApproveRadiology}
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                </div>

                {flowMode === 'stepper' && (
                  <div className="stepper-nav-footer">
                    <button
                      type="button"
                      className="btn-step-prev"
                      onClick={handlePrevStep}
                    >
                      <span>⬅ Back to Doctor Details</span>
                    </button>
                    <div className="stepper-progress-pill">Stage 2 of 5: Department Rights</div>
                    <button
                      type="button"
                      className="btn-step-next"
                      onClick={handleNextStep}
                    >
                      <span>Continue to Step 3: Signature & Preview</span>
                      <span>➔</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ===================================================================== */}
            {/* STAGE 3: HANDWRITTEN SIGNATURE IMAGE & QC SIMULATION                  */}
            {/* ===================================================================== */}
            {(flowMode === 'all' || currentStep === 3) && (
              <div className="doc-sig-section">
                <div className="step-guide-box">
                  <div className="step-guide-icon">✍️</div>
                  <div className="step-guide-text">
                    <h3>Step 3: Doctor Digital Signature & Live Report Preview (हस्ताक्षर अपलोड व प्रिव्यू)</h3>
                    <p>
                      Doctor ka signature image upload karein. Right side me live simulation dikhega ki patient report ke footer me Doctor Name, Degree, Reg No aur Sign kaisa stamp hoga.
                    </p>
                  </div>
                </div>

                <div className="doc-sig-section-title">
                  <span>Handwritten Signature Image & Quality Control</span>
                  {selectedProfile && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#64748b' }}>
                      Active Signature: <strong>Version {selectedProfile.currentVersion}</strong>
                    </span>
                  )}
                </div>

                <div className="doc-sig-canvas-zone">
                  {/* Upload Zone */}
                  <div>
                    <div
                      className="signature-dropzone"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleFileDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>✍️</div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e3a8a' }}>
                        Click or Drag & Drop Signature Image
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                        Supports PNG / JPG (Transparent background recommended). Auto-crops extra margins.
                      </div>
                    </div>

                    {uploadedBase64 && (
                      <div style={{ marginTop: '12px' }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                          Reason for Signature Version Update (Audit Log)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Registered new NMC clinical digital signature stamp"
                          value={uploadChangeReason}
                          onChange={(e) => setUploadChangeReason(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            fontSize: '0.82rem',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Live Preview Box */}
                  <div>
                    <div className="signature-preview-box">
                      {uploadedBase64 ? (
                        <div style={{ textAlign: 'center' }}>
                          <img
                            src={uploadedBase64}
                            alt="New Crop Preview"
                            style={{ maxHeight: '110px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                          <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700, marginTop: '4px' }}>
                            ✓ New Cropped Version Preview (Ready to Save)
                          </div>
                        </div>
                      ) : selectedProfile ? (
                        <div style={{ textAlign: 'center' }}>
                          <img
                            src={doctorSignatureService.getSignatureImageUrl(selectedProfile.profileId)}
                            alt="Current Signature"
                            style={{ maxHeight: '110px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                            Current Version {selectedProfile.currentVersion} Active
                          </div>
                        </div>
                      ) : (
                        <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No signature image uploaded yet.</div>
                      )}
                    </div>

                    {/* Report Footer Preview Simulation */}
                    <div style={{ marginTop: '12px' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>
                        Report Footer Preview Simulation:
                      </span>
                      <div className="report-footer-simulation">
                        <div className="sim-sig-container">
                          <img
                            src={uploadedBase64 || (selectedProfile ? doctorSignatureService.getSignatureImageUrl(selectedProfile.profileId) : '')}
                            alt="Sig"
                            style={{ maxHeight: '48px', objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <div className="sim-doctor-name">{formData.doctorName || 'Dr. Doctor Name'}</div>
                        <div className="sim-doctor-qual">{formData.qualification || 'MBBS, MD'}</div>
                        <div className="sim-doctor-reg">Reg: {formData.medicalRegNo || 'XXXX-XXXX'} ({formData.regAuthority || 'State Medical Council'})</div>
                      </div>
                    </div>
                  </div>
                </div>

                {flowMode === 'stepper' && (
                  <div className="stepper-nav-footer">
                    <button
                      type="button"
                      className="btn-step-prev"
                      onClick={handlePrevStep}
                    >
                      <span>⬅ Back to Department Rights</span>
                    </button>
                    <div className="stepper-progress-pill">Stage 3 of 5: Digital Signature</div>
                    <button
                      type="button"
                      className="btn-step-next"
                      onClick={handleNextStep}
                    >
                      <span>Continue to Step 4: Lab Technicians</span>
                      <span>➔</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ===================================================================== */}
            {/* STAGE 4: AUTHORIZED TECHNICIANS & DELEGATED APPROVAL HUB              */}
            {/* ===================================================================== */}
            {(flowMode === 'all' || currentStep === 4) && (
              <div className="doc-sig-section">
                <div className="step-guide-box">
                  <div className="step-guide-icon">👥</div>
                  <div className="step-guide-text">
                    <h3>Step 4: Delegated Technicians & Staff Approval Hub (लैब स्टाफ मैपिंग व अधिकार)</h3>
                    <p>
                      Is doctor ke bihaf par kaunsa lab technician result enter karega ya approve karega, yahan assign karein. Jab authorized technician report finalize karega toh doctor ka digital signature automatically stamp ho jayega.
                    </p>
                  </div>
                </div>

                <div className="doc-sig-section-title">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Authorized Technicians & Delegated Approval Hub</span>
                    <span className="badge-tech-count">
                      👥 {technicians.length} Authorized
                    </span>
                  </div>
                  {selectedProfile && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#64748b' }}>
                      Delegated on behalf of: <strong>{formData.doctorName}</strong>
                    </span>
                  )}
                </div>

                {!selectedProfile || selectedProfile.profileId === 0 ? (
                  <div className="doc-sig-tech-notice" style={{ padding: '24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.6rem', marginBottom: '8px' }}>💡</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0369a1', marginBottom: '6px' }}>
                      Save Doctor Profile to Unlock Technician Mapping
                    </div>
                    <p style={{ margin: '0 0 16px 0', color: '#475569', fontSize: '0.84rem' }}>
                      Technicians are linked to a registered Doctor Profile in the database. Click below to save {formData.doctorName || 'this doctor'} and immediately start mapping lab staff.
                    </p>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleSaveAndUnlockDelegation}
                      disabled={saving}
                      style={{ padding: '10px 22px', fontSize: '0.88rem' }}
                    >
                      {saving ? 'Registering Doctor...' : '💾 Save Doctor & Unlock Technician Mapping'}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Add New Technician Mapping Form */}
                    <div className="tech-mapping-form-card">
                      <div className="tech-mapping-form-title">
                        + Authorize New Technician / Delegated Approver
                      </div>
                      <div className="tech-mapping-inputs-grid">
                        <div className="doc-sig-field">
                          <label>Technician who may approve on this doctor's behalf *</label>
                          <select
                            value={newTech.technicianEmployeeId}
                            onChange={(e) => setNewTech({ ...newTech, technicianEmployeeId: e.target.value })}
                          >
                            <option value="">-- Select Technician to Authorize --</option>
                            {employees.map(emp => (
                              <option key={emp.employeeId} value={emp.employeeId}>
                                {emp.title ? `${emp.title} ` : ''}{emp.name} ({emp.designation || 'Staff'})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="doc-sig-field">
                          <label>Department Scope</label>
                          <select
                            value={newTech.departmentScope}
                            onChange={(e) => setNewTech({ ...newTech, departmentScope: e.target.value })}
                          >
                            <option value="ALL">All Departments (Lab-Wide)</option>
                            <option value="PATH">Pathology & Biochemistry</option>
                            <option value="MICRO">Microbiology & Serology</option>
                            <option value="BIOCHEM">Biochemistry Only</option>
                            <option value="HEMA">Hematology Only</option>
                            <option value="RADIO">Radiology & Imaging</option>
                          </select>
                        </div>

                        <div className="doc-sig-field">
                          <label>Delegated Authority Level *</label>
                          <select
                            value={newTech.approvalLevel}
                            onChange={(e) => setNewTech({ ...newTech, approvalLevel: e.target.value })}
                          >
                            <option value="APPROVE_VALIDATE">Approve & Final Release (Doctor Delegation)</option>
                            <option value="FORWARD">Forward for Review (Review Only)</option>
                            <option value="RESULT_ENTRY">Result Entry Only (Draft / Value Entry)</option>
                          </select>
                        </div>

                        <div className="doc-sig-field" style={{ justifyContent: 'center' }}>
                          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '18px' }}>
                            <input
                              type="checkbox"
                              checked={newTech.allowAutoSignature}
                              onChange={(e) => setNewTech({ ...newTech, allowAutoSignature: e.target.checked })}
                            />
                            <span style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a' }}>
                              Auto-Apply Doctor Signature
                            </span>
                          </label>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={handleAddTechnician}
                            disabled={techSaving || !newTech.technicianEmployeeId}
                            style={{
                              height: '38px',
                              width: '100%',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            {techSaving ? 'Saving...' : '+ Authorize Technician'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Mapped Technicians Table */}
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                        Currently Authorized Technicians ({technicians.length})
                      </div>
                      {techLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                          Loading technician mappings...
                        </div>
                      ) : technicians.length === 0 ? (
                        <div className="doc-sig-tech-empty">
                          <div style={{ fontSize: '1.4rem', marginBottom: '4px' }}>👥</div>
                          <div style={{ fontWeight: 600, color: '#475569' }}>No Technicians Authorized Yet</div>
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                            Select a lab technician above and click "+ Authorize Technician" to delegate approval rights under {formData.doctorName}.
                          </div>
                        </div>
                      ) : (
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                          <table className="tech-mapping-table">
                            <thead>
                              <tr>
                                <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                                <th>Technician Name & Designation</th>
                                <th>Department Scope</th>
                                <th>Approval Authority Level</th>
                                <th style={{ textAlign: 'center' }}>Auto-Signature Stamp</th>
                                <th style={{ textAlign: 'center' }}>Status</th>
                                <th style={{ width: '130px', textAlign: 'center' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {technicians.map((t, idx) => (
                                <tr key={t.mappingId}>
                                  <td style={{ textAlign: 'center', fontFamily: 'monospace', color: '#64748b' }}>
                                    {idx + 1}
                                  </td>
                                  <td>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{t.technicianName}</div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{t.designation || 'Lab Technologist'}</div>
                                  </td>
                                  <td>
                                    <span className="badge-dept" style={{ fontWeight: 600 }}>
                                      {t.departmentScope === 'ALL' ? 'All Departments' : t.departmentScope}
                                    </span>
                                  </td>
                                  <td>
                                    {t.approvalLevel === 'APPROVE_VALIDATE' && (
                                      <span className="badge-approval-approve">✓ Approve & Final Release</span>
                                    )}
                                    {t.approvalLevel === 'FORWARD' && (
                                      <span className="badge-approval-forward">↗ Forward for Review</span>
                                    )}
                                    {t.approvalLevel === 'RESULT_ENTRY' && (
                                      <span className="badge-approval-entry">✎ Result Entry Only</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {t.allowAutoSignature ? (
                                      <span className="badge-active" style={{ fontSize: '0.74rem' }}>
                                        ✓ Enabled
                                      </span>
                                    ) : (
                                      <span className="badge-inactive" style={{ fontSize: '0.74rem' }}>
                                        ✕ Disabled
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleToggleTechStatus(t.mappingId, t.isActive)}
                                      className={t.isActive ? 'badge-active' : 'badge-inactive'}
                                      style={{ border: 'none', cursor: 'pointer', padding: '3px 8px' }}
                                      title="Click to toggle status"
                                    >
                                      {t.isActive ? 'Active' : 'Inactive'}
                                    </button>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      className="btn-revoke-tech"
                                      onClick={() => handleDeleteTechMapping(t.mappingId, t.technicianName)}
                                      title="Revoke delegation authority"
                                    >
                                      Revoke
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {flowMode === 'stepper' && (
                  <div className="stepper-nav-footer">
                    <button
                      type="button"
                      className="btn-step-prev"
                      onClick={handlePrevStep}
                    >
                      <span>⬅ Back to Digital Signature</span>
                    </button>
                    <div className="stepper-progress-pill">Stage 4 of 5: Technician Delegation</div>
                    <button
                      type="button"
                      className="btn-step-next"
                      onClick={handleNextStep}
                    >
                      <span>Continue to Step 5: Final Review & Save</span>
                      <span>➔</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ===================================================================== */}
            {/* STAGE 5: EXECUTIVE CONFIGURATION REVIEW & FINAL CONFIRMATION          */}
            {/* ===================================================================== */}
            {(flowMode === 'all' || currentStep === 5) && (
              <div className="doc-sig-section">
                <div className="step-guide-box">
                  <div className="step-guide-icon">✅</div>
                  <div className="step-guide-text">
                    <h3>Step 5: Executive Configuration Review & Confirmation (फाइनल समीक्षा व एक्टिवेशन)</h3>
                    <p>
                      Doctor Profile, Department Rights, Digital Signature aur Authorized Technicians ka summary overview check karein aur profile finalize karein.
                    </p>
                  </div>
                </div>

                <div className="doc-sig-section-title">
                  <span>Configuration Summary & Final Verification</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#166534', fontWeight: 700 }}>
                    ✓ Ready to Finalize
                  </span>
                </div>

                {/* 4 Summary Cards Grid */}
                <div className="review-summary-grid">
                  {/* Card 1: Doctor Identity */}
                  <div className="review-summary-card">
                    <div className="review-card-title">
                      <span>👤 Doctor Identity</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Name:</span>
                      <span className="review-val">{formData.doctorName || 'Not Set'}</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Reg Number:</span>
                      <span className="review-val">{formData.medicalRegNo || 'Not Set'}</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Council:</span>
                      <span className="review-val">{formData.regAuthority || 'State Medical Council'}</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Qualifications:</span>
                      <span className="review-val">{formData.qualification || 'MBBS'}</span>
                    </div>
                  </div>

                  {/* Card 2: Department Rights */}
                  <div className="review-summary-card">
                    <div className="review-card-title">
                      <span>🔬 Department Rights</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Pathology & Biochem:</span>
                      <span className={formData.canApprovePathology ? 'badge-active' : 'badge-inactive'} style={{ fontSize: '0.75rem' }}>
                        {formData.canApprovePathology ? '✓ Authorized' : '✕ Disabled'}
                      </span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Microbiology & Serology:</span>
                      <span className={formData.canApproveMicrobiology ? 'badge-active' : 'badge-inactive'} style={{ fontSize: '0.75rem' }}>
                        {formData.canApproveMicrobiology ? '✓ Authorized' : '✕ Disabled'}
                      </span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Radiology & Imaging:</span>
                      <span className={formData.canApproveRadiology ? 'badge-active' : 'badge-inactive'} style={{ fontSize: '0.75rem' }}>
                        {formData.canApproveRadiology ? '✓ Authorized' : '✕ Disabled'}
                      </span>
                    </div>
                  </div>

                  {/* Card 3: Signature Stamp Simulation */}
                  <div className="review-summary-card">
                    <div className="review-card-title">
                      <span>✍️ Digital Signature Stamp</span>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px' }}>
                      <div className="sim-sig-container" style={{ minHeight: '44px' }}>
                        <img
                          src={uploadedBase64 || (selectedProfile ? doctorSignatureService.getSignatureImageUrl(selectedProfile.profileId) : '')}
                          alt="Signature Preview"
                          style={{ maxHeight: '42px', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>
                        {formData.doctorName}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {formData.medicalRegNo} ({formData.regAuthority})
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Technicians Delegation */}
                  <div className="review-summary-card">
                    <div className="review-card-title">
                      <span>👥 Delegated Staff</span>
                    </div>
                    <div className="review-row">
                      <span className="review-label">Authorized Technicians:</span>
                      <span className="review-val" style={{ color: '#0284c7' }}>{technicians.length} Mapped</span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '0.78rem', color: '#475569' }}>
                      {technicians.length === 0 ? (
                        <span style={{ color: '#94a3b8' }}>No technicians delegated yet.</span>
                      ) : (
                        technicians.slice(0, 3).map(t => (
                          <div key={t.mappingId} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                            <span>• {t.technicianName}</span>
                            <span style={{ fontWeight: 600, color: '#166534' }}>{t.departmentScope}</span>
                          </div>
                        ))
                      )}
                      {technicians.length > 3 && (
                        <div style={{ color: '#64748b', fontStyle: 'italic', marginTop: '4px' }}>
                          + {technicians.length - 3} more technicians
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Signature Version History Timeline Ledger */}
                {selectedProfile && history.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                      Signature Version History Audit Ledger ({history.length} versions)
                    </div>
                    <div className="doc-sig-history-timeline">
                      {history.map((h) => {
                        const isCurrent = h.signatureVersion === selectedProfile.currentVersion;
                        return (
                          <div key={h.historyId} className="history-card">
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="history-ver">Signature Version {h.signatureVersion}</span>
                                {isCurrent && <span className="badge-active" style={{ fontSize: '0.7rem' }}>CURRENT ACTIVE</span>}
                              </div>
                              <div className="history-date">
                                Uploaded by: <strong>{h.uploadedByName}</strong>
                                <br />
                                Effective: {new Date(h.effectiveFrom).toLocaleDateString()} {h.effectiveTo ? `to ${new Date(h.effectiveTo).toLocaleDateString()}` : '(Current Active)'}
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <img
                                src={doctorSignatureService.getHistorySignatureImageUrl(h.historyId)}
                                alt={`v${h.signatureVersion}`}
                                style={{ height: '40px', maxWidth: '120px', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <span style={{ fontSize: '0.72rem', color: isCurrent ? '#166534' : '#64748b', fontWeight: 600 }}>
                                {isCurrent ? 'ACTIVE' : 'ARCHIVED'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Final Submission Navigation Footer */}
                {flowMode === 'stepper' ? (
                  <div className="stepper-nav-footer">
                    <button
                      type="button"
                      className="btn-step-prev"
                      onClick={handlePrevStep}
                    >
                      <span>⬅ Back to Lab Technicians</span>
                    </button>
                    <div className="stepper-progress-pill">Stage 5 of 5: Complete & Save</div>
                    <button
                      type="submit"
                      className="btn-step-complete"
                      disabled={saving}
                    >
                      <span>💾 {saving ? 'Finalizing...' : 'Save & Activate Doctor Configuration'}</span>
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setViewMode('list')}
                    >
                      Cancel / View List
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Profile & Update Signature'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {/* Modal for Adding New Degree to Qualification Master */}
      {showAddQualModal && (
        <div className="qual-modal-backdrop" onClick={() => setShowAddQualModal(false)}>
          <div className="qual-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>
                🎓 Add New Degree to Qualification Master
              </h3>
              <button
                type="button"
                onClick={() => setShowAddQualModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>
            <div className="doc-sig-field">
              <label>Degree / Qualification Title *</label>
              <input
                type="text"
                placeholder="e.g. DM (Hematology), FRCPath (UK), DNB"
                value={newQualInput}
                onChange={(e) => setNewQualInput(e.target.value)}
                autoFocus
              />
              <small className="field-hint">
                This degree will be permanently added to the Medical Qualification Master and immediately selected.
              </small>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowAddQualModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCreateNewQualification}
                disabled={!newQualInput.trim()}
              >
                + Add to Master & Select
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorSignatureMasterPage;
